import { styled, alpha } from '@mui/material/styles';
import { Typography, Box, Button, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import Page from '../components/Page';
import Logo from '../components/Logo';
import loginHero from '../assets/lg.png';
import { LoginForm } from '../sections/auth/login';
import axios from '../axios';
import { handleAccess } from 'src/utils/responseHandler';
import { getURL } from 'src/utils/config';

const LOGIN_FONT_FAMILY = '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const LOGIN_ACCENT_FONT_FAMILY = '"IBM Plex Sans", "Segoe UI", Arial, sans-serif';
const LOGIN_HERO_FONT_FAMILY = '"Noto Sans Chakma", "Segoe UI", sans-serif';

const RootStyle = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  backgroundColor: '#d9dce2',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const HeroPanel = styled('div')(({ theme }) => ({
  flex: '0 0 56%',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
  padding: theme.spacing(3.5, 4, 4.5),
  background:
    'radial-gradient(circle at 14% 12%, rgba(255,255,255,0.85), transparent 22%), linear-gradient(180deg, #d7dae0 0%, #d0d4dc 100%)',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    minHeight: 'auto',
    width: '100%',
    padding: theme.spacing(2.5, 2.5, 3),
  },
}));

const AuthPanel = styled('div')(({ theme }) => ({
  flex: 1,
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  padding: theme.spacing(20, 4, 5),
  background: 'linear-gradient(180deg, #fdfdfd 0%, #f6f7fa 100%)',
  borderTopLeftRadius: 34,
  borderBottomLeftRadius: 34,
  boxShadow: `-24px 0 64px ${alpha('#202632', 0.08)}`,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    minHeight: 'auto',
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    borderBottomLeftRadius: 0,
    padding: theme.spacing(3, 2.5, 4),
  },
}));

const HeroFrame = styled('div')(({ theme }) => ({
  width: '100%',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 520,
    height: 520,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0) 68%)',
    filter: 'blur(8px)',
  },
  [theme.breakpoints.down('md')]: {
    minHeight: 280,
    '&::before': {
      width: 320,
      height: 320,
    },
  },
}));

const AuthHeader = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 430,
  margin: '0 auto',
  paddingTop: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    paddingTop: 0,
  },
}));

const AuthFormSection = styled('div')(({ theme }) => ({
  width: '100%',
  flex: 1,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'stretch',
  paddingTop: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(2.5),
  },
}));

const ExploreButton = styled(Button)(({ theme }) => ({
  alignSelf: 'center',
  color: '#f7f9fc',
  background: 'linear-gradient(180deg, #27313d 0%, #1c242f 100%)',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.35)',
  boxShadow: '0 8px 20px rgba(23, 31, 42, 0.18)',
  padding: theme.spacing(1.05, 2),
  textTransform: 'none',
  fontSize: 15,
  fontWeight: 600,
  '&:hover': {
    background: 'linear-gradient(180deg, #2d3744 0%, #222b36 100%)',
  },
}));

export default function Login({ setToken, setAccess, setUserType, setuserData, setAuthId }) {
  const [isRedirect, setRedirect] = useState(true);
  const urlParams = new URLSearchParams(window.location.search);
  const authentication = 0;
  const [subTitle, setSubTitle] = useState('');
  const [subTitleColor, setSubTitleColor] = useState('');

  const jsonToFormData = (data) => {
    const formdata = new FormData();
    for (const x in data) formdata.append(x, data[x]);
    return formdata;
  };

  const handleLogin = (body, type = 'form', date = null, shift = null, report_type = null) => {
    setToken(null);
    setAccess(false);
    setUserType(null);

    setuserData({
      access_token: null,
      token_type: null,
      ID: null,
      IsLicenseBase: null,
      IsAgentBased: null,
      name: null,
      displayname: null,
      logo_url: null,
      user_type: null,
      client_id: null,
    });

    axios({
      method: 'post',
      url: getURL('token'),
      data: body,
      validateStatus(status) {
        return status >= 200 || status === 400;
      },
    }).then((response) => {
      if (
        (response.status === 200 || response.status === 201) &&
        'access_token' in response.data &&
        ![3, 4].includes(parseInt(response.data.user_type, 10))
      ) {
        const res = response.data;
        for (const x in res) {
          localStorage.setItem(x, res[x]);
        }
        const AUTH_TOKEN = `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`;
        axios.defaults.headers.common.Authorization = AUTH_TOKEN;

        setuserData({
          access_token: res.access_token,
          token_type: res.token_type,
          ID: res.ID,
          IsLicenseBase: res.IsLicenseBase,
          IsAgentBased: res.IsAgentBased,
          name: res.name,
          displayname: res.displayname,
          logo_url: res.logo_url,
          user_type: res.user_type,
          client_id: res.client_id,
        });

        setUserType(parseInt(response.data.user_type, 10));
        setToken(localStorage.getItem('access_token'));
        handleAccess(setAccess, AUTH_TOKEN);
      }
    });
  };

  useEffect(() => {
    const username = urlParams.get('username');
    const password = urlParams.get('password');
    const date = urlParams.get('date');
    const shift = urlParams.get('shift');
    const type = urlParams.get('type');

    if (username && password) {
      handleLogin(
        jsonToFormData({
          username,
          password,
          grant_type: 'password',
        }),
        'url',
        date,
        shift,
        type
      );
    } else {
      setRedirect(false);
    }
  }, []);

  return (
    <Page title="Login">
      {!isRedirect && (
        <RootStyle>
          <HeroPanel>
          
            <HeroFrame>
              <Box
                component="img"
                src={loginHero}
                alt="VIDISKY monitoring devices"
                sx={{
                  width: '80%',
                  maxWidth: 'none',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1,
                  filter: 'drop-shadow(0 30px 40px rgba(60, 70, 84, 0.14))',
                }}
              />
            </HeroFrame>

            <Box sx={{ width: '100%', maxWidth: 620, mx: 'auto', textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                sx={{
                  fontFamily: LOGIN_HERO_FONT_FAMILY,
                  color: '#232c39',
                  fontSize: { xs: 24, sm: 30, md: 36 },
                  lineHeight: 1.12,
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                  mb: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Secure your properties. In real time.
              </Typography>
              <Typography
                sx={{
                  fontFamily: LOGIN_HERO_FONT_FAMILY,
                  color: '#667080',
                  fontSize: { xs: 20, md: 21 },
                  lineHeight: 1.45,
                  maxWidth: 560,
                  letterSpacing: '0.03em',
                  mx: { xs: 'auto', md: 0 },
                  textAlign: 'center',
                }}
              >
                AI-powered monitoring with human verification so you only act when it matters.
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                sx={{ mt: 4, color: '#3f4958', alignItems: 'center', justifyContent: 'center' }}
              >
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <SecurityOutlinedIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontFamily: LOGIN_HERO_FONT_FAMILY, fontSize: 16, fontWeight: 500 }}>
                    Enterprise-grade security
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <InsightsOutlinedIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontFamily: LOGIN_HERO_FONT_FAMILY, fontSize: 16, fontWeight: 500 }}>
                    Real-time insights
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </HeroPanel>

          <AuthPanel>
            <AuthHeader>
  <Box
    sx={{
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <Logo
      sx={{
        width: { xs: 160, sm: 180, md: 200 },
        height: 'auto',
        flexShrink: 0,
        mx: 'auto',
        alignSelf: 'center',
        mb: { xs: 2, sm: 3 },
      }}
    />

    <Typography
      sx={{
        fontFamily: LOGIN_ACCENT_FONT_FAMILY,
        color: '#212936',
        fontSize: { xs: 34, md: 38 },
        lineHeight: 1.12,
        fontWeight: 500,
        letterSpacing: '-0.03rem',
        mb: 1,
      }}
    >
      Sign in to VIDISKY
    </Typography>

    <Typography
      sx={{
        fontFamily: LOGIN_FONT_FAMILY,
        color: '#6b7280',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: 1.35,
      }}
    >
      Access your monitoring dashboard
    </Typography>
  </Box>
</AuthHeader>

            <AuthFormSection>
              <LoginForm
                setToken={setToken}
                setAccess={setAccess}
                setUserType={setUserType}
                setuserData={setuserData}
                authentication={authentication}
                setSubTitle={setSubTitle}
                setSubTitleColor={setSubTitleColor}
                setAuthId={setAuthId}
              />
            </AuthFormSection>
          </AuthPanel>
        </RootStyle>
      )}
    </Page>
  );
}

Login.propTypes = {
  setToken: PropTypes.func.isRequired,
  setAccess: PropTypes.func,
  setUserType: PropTypes.func,
  setuserData: PropTypes.func,
  setAuthId: PropTypes.func,
};
